import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { take, retry } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface XenditFeeConfig {
  fee_rate:  number | null;
  fixed_fee: number | null;
}

const DEFAULTS: XenditFeeConfig = {
  fee_rate:  3.5,
  fixed_fee: 15,
};

@Injectable({ providedIn: 'root' })
export class XenditFeeService {
  private readonly config$ = new BehaviorSubject<XenditFeeConfig>(DEFAULTS);
  private _loadFailed = false;

  get loadFailed(): boolean { return this._loadFailed; }

  constructor(private http: HttpClient) {
    this.reload();
  }

  reload(): void {
    this.http
      .get<XenditFeeConfig>(`${environment.baseApiUrl}/xendit/fee-config`)
      .pipe(
        retry({ count: 2, delay: 1000 }),
        take(1),
      )
      .subscribe({
        next: (config) => this.config$.next(config),
        error: (err) => {
          this._loadFailed = true;
          console.error('[XenditFeeService] Failed to load fee config, using defaults', err);
        },
      });
  }

  get feeRate(): number | null {
    return this.config$.getValue().fee_rate;
  }

  get fixedFee(): number | null {
    return this.config$.getValue().fixed_fee;
  }

  calculate(amount: number): number {
    const rate  = this.feeRate  ?? 0;
    const fixed = this.fixedFee ?? 0;
    return amount * (rate / 100) + fixed;
  }

  hasFee(): boolean {
    return this.feeRate !== null || this.fixedFee !== null;
  }

  label(): string {
    const ratePart  = this.feeRate  !== null ? `${parseFloat(this.feeRate.toFixed(2))}%` : null;
    const fixedPart = this.fixedFee !== null ? `₱${this.fixedFee}` : null;
    const parts = [ratePart, fixedPart].filter(Boolean).join(' + ');
    return parts ? `Service Fee (${parts})` : 'Service Fee';
  }
}
